import style from './style.scss';
import {
	convertSpeedUnitToWindRunUnit, formatPressure, formatRainfall, formatRainRate,
	formatTemperature, formatWindGust, formatWindSpeed, getDateTimeFormatConverter
} from '../../utils/formatters';
import { convertWindBearing8 } from '../../utils/convert-wind-bearing';
import handleLoadingAndError from '../handle-loading-and-error';
import Table from '../table';
import CardDateLabel from '../card-date-label';
import {
	HighlightHighTemperature,
	HighlightLowTemperature,
	HighlightMeanTemperature,
	HighlightRainfall,
	HighlightWindGust
} from '../highlight-values';

/**
 * The DailySummary component is used to display a tabular summary of a particular day's
 * weather data summary.
 *
 * props:
 * - data: An object containing daily summary data, as returned by the api
 */

const DailySummary = (props, context) => {

	const units = context.userSettings.units;

	const formatters = {
		date:        getDateTimeFormatConverter('%Y-%m-%d', '%-d %B'),
		time:        getDateTimeFormatConverter('%H:%M', '%X'),
		temperature: t => formatTemperature(t, units.temperature),
		rain:        r => formatRainfall(r, units.rain),
		rainRate:    r => formatRainRate(r, units.rain),
		windSpeed:   s => formatWindSpeed(s, units.speed),
		windGust:    g => formatWindGust(g, units.speed),
		pressure:    p => formatPressure(p, units.pressure)
	};

	const { data, excludeKeyStats } = props;

	let highTemperature, lowTemperature, highWindGust, rainfall;

	if (excludeKeyStats) {

		highTemperature = null;
		lowTemperature = null;
		highWindGust = null;
		rainfall = null;

	} else {

		highTemperature = (
			<tr>
				<td>High temperature</td>
				<td>
					<HighlightHighTemperature>
						{formatters.temperature(data.highTemperature) + `°${units.temperature}`}
					</HighlightHighTemperature>
				</td>
				<td>
					{formatters.time(data.highTemperatureTime)}
				</td>
			</tr>
		);

		lowTemperature = (
			<tr>
				<td>Low temperature</td>
				<td>
					<HighlightLowTemperature>
						{formatters.temperature(data.lowTemperature) + `°${units.temperature}`}
					</HighlightLowTemperature>
				</td>
				<td>
					{formatters.time(data.lowTemperatureTime)}
				</td>
			</tr>
		);

		const highWindGustDirection = data.highWindGust > 0
			? convertWindBearing8(data.highWindGustBearing) + ' '
			: '';

		highWindGust = (
			<tr>
				<td>High wind gust</td>
				<td>
					<HighlightWindGust>
						{`${highWindGustDirection}${formatters.windGust(data.highWindGust)} ${units.speed}`}
					</HighlightWindGust>
				</td>
				<td>
					{formatters.time(data.highWindGustTime)}
				</td>
			</tr>
		);

		rainfall = (
			<tr>
				<td>Total rainfall</td>
				<td>
					<HighlightRainfall>
						{formatters.rain(data.rainfall) + ` ${units.rain}`}
					</HighlightRainfall>
				</td>
				<td />
			</tr>
		);

	}

	return (
		<div class={style.daily_summary}>
			<CardDateLabel date={formatters.date(data.date)} />
			<Table>
				<tbody>

				{ highTemperature }
				{ lowTemperature }

				<tr>
					<td>Mean temperature</td>
					<td>
						<HighlightMeanTemperature>
							{formatters.temperature(data.avgTemperature) + `°${units.temperature}`}
						</HighlightMeanTemperature>
					</td>
					<td />
				</tr>

				{ highWindGust }

				<tr>
					<td>High avg speed</td>
					<td>
						{formatters.windSpeed(data.highWindSpeed) + ` ${units.speed}`}
					</td>
					<td>
						{formatters.time(data.highWindSpeedTime)}
					</td>
				</tr>

				<tr>
					<td>Wind run</td>
					<td>
						{formatters.windSpeed(data.windRun) + ` ${convertSpeedUnitToWindRunUnit(units.speed)}`}
					</td>
					<td />
				</tr>

				{ rainfall }

				<tr>
					<td>High rain rate</td>
					<td>
						{formatters.rainRate(data.highRainRate) + ` ${units.rain}/hr`}
					</td>
					<td>
						{formatters.time(data.highRainRateTime)}
					</td>
				</tr>

				<tr>
					<td>High hourly rain</td>
					<td>
						{formatters.rain(data.highHourlyRain) + ` ${units.rain}`}
					</td>
					<td>
						{formatters.time(data.highHourlyRainTime)}
					</td>
				</tr>

				<tr>
					<td>High humidity</td>
					<td>
						{data.highHumidity}%
					</td>
					<td>
						{formatters.time(data.highHumidityTime)}
					</td>
				</tr>

				<tr>
					<td>Low humidity</td>
					<td>
						{data.lowHumidity}%
					</td>
					<td>
						{formatters.time(data.lowHumidityTime)}
					</td>
				</tr>

				<tr>
					<td>High dewpoint</td>
					<td>
						{formatters.temperature(data.highDewpoint) + `°${units.temperature}`}
					</td>
					<td>
						{formatters.time(data.highDewpointTime)}
					</td>
				</tr>

				<tr>
					<td>Low dewpoint</td>
					<td>
						{formatters.temperature(data.lowDewpoint) + `°${units.temperature}`}
					</td>
					<td>
						{formatters.time(data.lowDewpointTime)}
					</td>
				</tr>

				<tr>
					<td>High pressure</td>
					<td>
						{formatters.pressure(data.highPressure) + ` ${units.pressure}`}
					</td>
					<td>
						{formatters.time(data.highPressureTime)}
					</td>
				</tr>

				<tr>
					<td>Low pressure</td>
					<td>
						{formatters.pressure(data.lowPressure) + ` ${units.pressure}`}
					</td>
					<td>
						{formatters.time(data.lowPressureTime)}
					</td>
				</tr>

				</tbody>
			</Table>
		</div>
	);
};

// Export a wrapped component, where loading state and error are already handled
export default handleLoadingAndError(DailySummary);



// WEBPACK FOOTER //
// ./components/daily-summary/index.js